import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * Store для управления состоянием импорта Excel файлов
 * Сохраняет состояние между переходами по страницам
 */
export const useFilesStore = defineStore('files', () => {
  // ========== Состояние загрузки файла ==========
  const selectedExcelFile = ref(null)
  const isUploadingExcel = ref(false)

  // ========== Данные Excel и маппинг ==========
  // Изменено с reactive на ref для корректной работы с storeToRefs и персистентностью
  const excelData = ref({
    columns: null,
    mapping: {},
    sample_data: [],
    structure_info: null,
    extracted_images: {},
    column_unique_values: {},
    value_mapping_rules: {},
    file_token: null,
    protected_fields: []  // Поля, которые НЕ обновляются при конфликте
  })

  // ========== Процесс импорта ==========
  const currentStep = ref(1) // 1 - маппинг, 2 - нормализация
  const isConfirming = ref(false)
  const isPreviewLoading = ref(false)
  const previewResult = ref(null)
  const confirmationResults = ref(null)

  // ========== Value Mapping ==========
  const valueMappings = ref({})
  const unresolvedValueMappings = ref([])
  const staticAttributeUniqueValues = ref({})
  const selectedStaticAttributes = ref([])

  // ========== Прогресс импорта ==========
  const importInProgress = ref(false)
  const importError = ref(null)
  const importStartedAt = ref(null)
  const importDurationMs = ref(null)
  const showImportModal = ref(false)

  const createDefaultImportSteps = () => ([
    {
      key: 'prepare',
      title: 'Подготовка файла',
      description: 'Проверка маппинга и статичных атрибутов',
      status: 'pending',
      note: null
    },
    {
      key: 'normalize',
      title: 'Обработка и валидация',
      description: 'Нормализация данных и проверка артикулов',
      status: 'pending',
      note: null
    },
    {
      key: 'import',
      title: 'Импорт в базу',
      description: 'Добавление новых записей и обновление существующих',
      status: 'pending',
      note: null
    },
    {
      key: 'post',
      title: 'Финализация',
      description: 'Фоновые задачи и итоговый отчёт',
      status: 'pending',
      note: null
    }
  ])

  const importSteps = ref(createDefaultImportSteps())

  // ========== Computed ==========
  const hasActiveImport = computed(() => {
    return excelData.value.columns !== null || 
           currentStep.value > 1 || 
           previewResult.value !== null ||
           confirmationResults.value !== null
  })

  const importCompleted = computed(() => {
    return !importInProgress.value && 
           !importError.value && 
           !!confirmationResults.value
  })

  const hasRejectedRows = computed(() => {
    return Boolean(
      confirmationResults.value?.rejected_count > 0 &&
      confirmationResults.value?.rejected_file_token
    )
  })

  // ========== Методы ==========
  
  /**
   * Сброс всего состояния импорта
   */
  function resetImportState() {
    selectedExcelFile.value = null
    isUploadingExcel.value = false
    
    excelData.value.columns = null
    excelData.value.mapping = {}
    excelData.value.sample_data = []
    excelData.value.structure_info = null
    excelData.value.extracted_images = {}
    excelData.value.column_unique_values = {}
    excelData.value.value_mapping_rules = {}
    excelData.value.file_token = null
    excelData.value.protected_fields = []
    
    currentStep.value = 1
    isConfirming.value = false
    isPreviewLoading.value = false
    previewResult.value = null
    confirmationResults.value = null
    
    valueMappings.value = {}
    unresolvedValueMappings.value = []
    staticAttributeUniqueValues.value = {}
    selectedStaticAttributes.value = []
    
    resetImportProgress()
  }

  /**
   * Сброс прогресса импорта
   */
  function resetImportProgress() {
    importSteps.value = createDefaultImportSteps()
    importError.value = null
    importInProgress.value = false
    importStartedAt.value = null
    importDurationMs.value = null
    showImportModal.value = false
  }

  /**
   * Установка статуса шага импорта
   */
  function setStepStatus(key, status, extra = {}) {
    importSteps.value = importSteps.value.map(step =>
      step.key === key ? { ...step, status, ...extra } : step
    )
  }

  /**
   * Переход к следующему шагу
   */
  function goToNextStep() {
    if (currentStep.value < 2) {
      currentStep.value++
    }
  }

  /**
   * Возврат к предыдущему шагу
   */
  function goToPreviousStep() {
    if (currentStep.value > 1) {
      currentStep.value--
      previewResult.value = null
    }
  }

  /**
   * Обновление маппинга колонок
   */
  function updateMapping(dbField, excelColumn) {
    if (excelColumn) {
      excelData.value.mapping[dbField] = excelColumn
    } else {
      delete excelData.value.mapping[dbField]
    }
    previewResult.value = null
  }

  /**
   * Очистка всего маппинга
   */
  function clearMapping() {
    excelData.value.mapping = {}
    previewResult.value = null
  }

  return {
    // State
    selectedExcelFile,
    isUploadingExcel,
    excelData,
    currentStep,
    isConfirming,
    isPreviewLoading,
    previewResult,
    confirmationResults,
    valueMappings,
    unresolvedValueMappings,
    staticAttributeUniqueValues,
    selectedStaticAttributes,
    importInProgress,
    importError,
    importStartedAt,
    importDurationMs,
    importSteps,
    showImportModal,
    
    // Computed
    hasActiveImport,
    importCompleted,
    hasRejectedRows,
    
    // Methods
    resetImportState,
    resetImportProgress,
    setStepStatus,
    goToNextStep,
    goToPreviousStep,
    updateMapping,
    clearMapping,
    createDefaultImportSteps
  }
}, {
  // Персистентность: сохраняем состояние в sessionStorage
  // При закрытии вкладки браузера состояние очистится
  // При переходах между страницами - сохранится
  persist: {
    key: 'ai-database-files',
    storage: sessionStorage,
    paths: [
      'excelData',
      'currentStep',
      'valueMappings',
      'staticAttributeUniqueValues',
      'selectedStaticAttributes',
      'previewResult',
      'confirmationResults'
    ]
  }
})
